import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import {
  CancelCommandDirective,
  CellClickEvent,
  CellCloseEvent,
  CellTemplateDirective,
  ColumnComponent,
  EditTemplateDirective,
  FocusableDirective,
  GridComponent,
  GridToolbarFocusableDirective,
  NoRecordsTemplateDirective,
  RemoveCommandDirective,
  SaveCommandDirective,
  ToolbarTemplateDirective
} from '@progress/kendo-angular-grid';
import { uniq } from 'es-toolkit/compat';
import { filter, take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { confirmationModalOpener } from 'src/app/features/modals/confirmation-modal/confirmation-modal.modal';
import { ClientDto } from 'src/app/models/client/client-dto';
import { ModalConfig } from 'src/app/models/modal-config';
import { NotificationModuleType } from 'src/app/models/notification/notification-module-type';
import { NotificationRuleBaseDto } from 'src/app/models/notification/notification-rule-base-dto';
import { NotificationRuleWorkItemCreateDto } from 'src/app/models/notification/notification-rule-work-item-create-dto';
import { NotificationRuleWorkItemDto } from 'src/app/models/notification/notification-rule-work-item-dto';
import { ProcessElementDto } from 'src/app/models/process/process-element-dto';
import { AlertService } from 'src/app/services/alert.service';
import { NotificationRulesService } from 'src/app/services/api/notification-api';
import { CloneService } from 'src/app/services/local/clone.service';
import { ModalOpenerService } from 'src/app/services/modal-opener.service';
import { NotificationRuleWorkItemWrapper } from '../../model/notification-rule-work-item-wrapper';
import { NotificationRulesMetadataIds } from '../../model/notification-rules-metadata-ids';
import { NotificationRulesMetadataService } from '../../services/notification-rules-metadata.service';
import {
  notificationsSetupClientIdsModalOpener,
  NotificationsSetupClientIdsModalOutput
} from '../notifications-setup-client-ids-modal/notifications-setup-client-ids-modal.modal';
import {
  notificationsSetupProcessIdsModalOpener,
  NotificationsSetupProcessIdsModalOutput
} from '../notifications-setup-process-ids-modal/notifications-setup-process-ids-modal.modal';

@Component({
  selector: 'app-notifications-setup-work-items',
  templateUrl: './notifications-setup-work-items.component.html',
  styleUrl: './notifications-setup-work-items.component.scss',
  providers: [NotificationRulesMetadataService],
  imports: [
    GridComponent,
    ToolbarTemplateDirective,
    ButtonComponent,
    ColumnComponent,
    CellTemplateDirective,
    EditTemplateDirective,
    MultiSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    GridToolbarFocusableDirective,
    FocusableDirective,
    RemoveCommandDirective,
    SaveCommandDirective,
    CancelCommandDirective,
    NoRecordsTemplateDirective
  ]
})
export class NotificationsSetupWorkItemsComponent implements OnInit {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly modalOpener = inject(ModalOpenerService);
  private readonly cloneService = inject(CloneService);
  private readonly notificationRulesService = inject(NotificationRulesService);
  private readonly notificationRulesMetadataService = inject(
    NotificationRulesMetadataService
  );
  private readonly alertService$ = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly grid = viewChild.required(GridComponent);

  ruleWrappers: NotificationRuleWorkItemWrapper[] = [];
  statuses: FormStatus[] = [
    // {
    //   name: 'None',
    //   value: 0
    // },
    {
      name: 'Error',
      value: 1
    },
    {
      name: 'Not ready',
      value: 2
    },
    {
      name: 'Not ready (late)',
      value: 3
    },
    {
      name: 'Queued',
      value: 4
    },
    {
      name: 'Ready',
      value: 5
    },
    {
      name: 'Ready (late)',
      value: 6
    },
    {
      name: 'Done',
      value: 7
    },
    {
      name: 'Done (late)',
      value: 8
    }
  ];

  protected notificationRuleToAdd: NotificationRuleWorkItemWrapper;

  ngOnInit(): void {
    this.notificationRulesService
      .getNotificationRules$(NotificationModuleType.WorkItem)
      .pipe(
        switchMap((rules: NotificationRuleBaseDto[]) => {
          const workItemRules = rules as NotificationRuleWorkItemDto[];
          const metadataIds = this.extractMetadataIdsFromRules(workItemRules);
          return this.notificationRulesMetadataService
            .getFilterMetadata$(metadataIds)
            .pipe(
              map(metadataValues => ({
                rules: workItemRules,
                metadataValues
              }))
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ rules, metadataValues }) => {
        this.ruleWrappers = rules.map(rule =>
          NotificationRuleWorkItemWrapper.createFrom(
            rule,
            metadataValues,
            this.cloneService
          )
        );
      });
  }

  canUpdate(
    isNew: boolean,
    dataItem: NotificationRuleWorkItemWrapper
  ): boolean {
    const result = !isNew && dataItem?.hasChanges;
    return result;
  }

  private extractMetadataIdsFromRules(
    rules: NotificationRuleWorkItemDto[]
  ): NotificationRulesMetadataIds {
    const result = new NotificationRulesMetadataIds();

    const clientIds = [];
    const processNodeIds = [];
    const organizationIds = [];

    for (const rule of rules) {
      if (rule.clientIds?.length > 0) {
        clientIds.push(...rule.clientIds);
      }
      if (rule.processNodeIds?.length > 0) {
        processNodeIds.push(...rule.processNodeIds);
      }
    }

    result.clientIds = uniq(clientIds);
    result.processNodeIds = uniq(processNodeIds);
    result.organizationIds = uniq(organizationIds);

    return result;
  }

  displayStatuses(ruleStatuses: number[]): string {
    const names =
      ruleStatuses
        ?.map(rs => this.statuses.find(s => s.value == rs)?.name ?? '')
        ?.filter(name => name != '') ?? [];
    return names.join(', ');
  }

  displayProcessNodes(processNodes: ProcessElementDto[]): string {
    const nodeNames = processNodes?.map(pn => pn.name) ?? [];
    return nodeNames.join(', ');
  }

  displayClients(clients: ClientDto[]): string {
    const clientNames = clients?.map(c => c.name) ?? [];
    if (clientNames.length > 5) {
      const result = clientNames.slice(0, 5).join(',').concat(' ...');
      return result;
    }
    return clientNames.join(', ');
  }

  private createNotificationRule(): NotificationRuleWorkItemWrapper {
    const rule = new NotificationRuleWorkItemDto();
    rule.type = NotificationModuleType.WorkItem;
    rule.clientIds = [];
    rule.processNodeIds = [];
    rule.statuses = [];

    const ruleWrapper = NotificationRuleWorkItemWrapper.createFrom(
      rule,
      null,
      this.cloneService
    );

    return ruleWrapper;
  }

  cellClick(event: CellClickEvent): void {
    const notificationRuleWrapper: NotificationRuleWorkItemWrapper =
      this.notificationRuleToAdd ?? event.dataItem;

    if (event.column.field == 'clientIds') {
      this.modalOpener
        .open(notificationsSetupClientIdsModalOpener, {
          notificationRuleId: notificationRuleWrapper.id,
          clients: this.cloneService.deepClone(notificationRuleWrapper.clients)
        })
        .closed$.pipe(
          take(1),
          filter(
            (output): output is NotificationsSetupClientIdsModalOutput =>
              output?.saveAction != null
          )
        )
        .subscribe((output: NotificationsSetupClientIdsModalOutput) => {
          notificationRuleWrapper.clients = output.saveAction.value;
          notificationRuleWrapper.hasChanges = true;
        });

      return;
    }

    if (event.column.field == 'processNodeIds') {
      this.modalOpener
        .open(notificationsSetupProcessIdsModalOpener, {
          notificationRuleId: notificationRuleWrapper.id,
          processNodes: this.cloneService.deepClone(
            notificationRuleWrapper.processNodes
          )
        })
        .closed$.pipe(
          take(1),
          filter(
            (output): output is NotificationsSetupProcessIdsModalOutput =>
              output?.saveAction != null
          )
        )
        .subscribe((output: NotificationsSetupProcessIdsModalOutput) => {
          notificationRuleWrapper.processNodes = output.saveAction.value;
          notificationRuleWrapper.hasChanges = true;
        });

      return;
    }

    if (!event.isEdited && !!notificationRuleWrapper.id) {
      event.sender.editCell(
        event.rowIndex,
        event.columnIndex,
        this.createFormGroup(notificationRuleWrapper)
      );
    }
  }

  cellClose(event: CellCloseEvent): void {
    const formGroup: UntypedFormGroup = event.formGroup;
    if (formGroup.valid) {
      const ruleWrapper: NotificationRuleWorkItemWrapper = event.dataItem;
      const formValue: RuleFormValue = formGroup.value;
      this.initWrapper(ruleWrapper, formValue);
      ruleWrapper.hasChanges = true;
    } else {
      event.preventDefault();
    }
  }

  onStatusesValueChange(
    dataItem: NotificationRuleWorkItemWrapper,
    statuses: number[]
  ): void {
    dataItem.statuses = statuses;
  }

  addNotification(): void {
    this.notificationRuleToAdd = this.createNotificationRule();
    this.grid().addRow(this.createFormGroup(this.notificationRuleToAdd));
  }

  private ruleIsValid(ruleWrapper: NotificationRuleWorkItemWrapper): boolean {
    const hasClients = ruleWrapper.clients?.length > 0;
    const hasProcessNodes = ruleWrapper.processNodes?.length > 0;
    const hasStatuses = ruleWrapper.statuses?.length > 0;

    const result = hasClients && hasProcessNodes && hasStatuses;

    return result;
  }

  saveNew(event: any): void {
    const sender: GridComponent = event.sender;
    const formGroup: UntypedFormGroup = event.formGroup;
    const rowIndex: number = event.rowIndex;

    if (formGroup.valid) {
      const ruleWrapper: NotificationRuleWorkItemWrapper =
        this.createNotificationRule();
      const formValue: RuleFormValue = formGroup.value;
      this.initWrapper(ruleWrapper, formValue);

      if (!this.ruleIsValid(ruleWrapper)) {
        this.alertService$.showWarning(
          'At least one Client and ProcessNode has to be selected in combination with Status'
        );
        return;
      }

      const createDto = this.buildCreateDto(ruleWrapper);
      this.notificationRulesService
        .addNotificationRule$(createDto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(result => {
          this.alertService$.showSuccess(`Rule ${ruleWrapper.name} created`);
          ruleWrapper.initialRule = result as NotificationRuleWorkItemDto;
          ruleWrapper.id = result.id;
          ruleWrapper.hasChanges = false;
          this.ruleWrappers = [ruleWrapper, ...this.ruleWrappers];
        });

      sender.closeRow(rowIndex);
    }

    this.notificationRuleToAdd = null;
  }

  save(dataItem: NotificationRuleWorkItemWrapper): void {
    if (!this.ruleIsValid(dataItem)) {
      this.alertService$.showWarning(
        'At least one Client and ProcessNode has to be selected in combination with Status'
      );
      return;
    }
    const updatePayload = this.buildUpdateDto(dataItem);
    this.notificationRulesService
      .updateNotificationRule$(updatePayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(_result => {
        dataItem.hasChanges = false;
        this.alertService$.showSuccess(`Rule ${dataItem.name} updated`);
      });
  }

  remove(event: any): void {
    const rule: NotificationRuleWorkItemWrapper = event.dataItem;
    this.modalOpener
      .open(confirmationModalOpener, {
        modalConfig: new ModalConfig(
          `Are you sure you want to delete this rule (${rule.name}) ?`,
          'Delete'
        )
      })
      .closed$.pipe(
        take(1),
        switchMap(() =>
          this.notificationRulesService.deleteNotificationRule$(
            rule.id,
            NotificationModuleType.WorkItem
          )
        )
      )
      .subscribe((result: boolean) => {
        if (result) {
          this.ruleWrappers = this.ruleWrappers.filter(rw => rw.id != rule.id);
        }
      });
  }

  cancel({ sender, rowIndex }): void {
    this.notificationRuleToAdd = null;
    sender.closeRow(rowIndex);
  }

  private buildCreateDto(
    ruleWrapper: NotificationRuleWorkItemWrapper
  ): NotificationRuleWorkItemCreateDto {
    const result = new NotificationRuleWorkItemCreateDto();
    result.name = ruleWrapper.name;
    result.type = NotificationModuleType.WorkItem;
    result.clientIds = ruleWrapper.clients?.map(c => c.id);
    result.processNodeIds = ruleWrapper.processNodes?.map(
      pn => `${pn.compositeId}`
    );
    result.statuses = ruleWrapper.statuses;
    return result;
  }

  private buildUpdateDto(
    ruleWrapper: NotificationRuleWorkItemWrapper
  ): NotificationRuleWorkItemDto {
    const result = new NotificationRuleWorkItemDto();
    result.id = ruleWrapper.id;
    result.name = ruleWrapper.name;
    result.type = NotificationModuleType.WorkItem;
    result.clientIds = ruleWrapper.clients?.map(c => c.id);
    result.processNodeIds = ruleWrapper.processNodes?.map(
      pn => `${pn.compositeId}`
    );
    result.statuses = ruleWrapper.statuses;
    return result;
  }

  private initWrapper(
    wrapper: NotificationRuleWorkItemWrapper,
    formValue: RuleFormValue
  ): void {
    wrapper.name = formValue.name;
    wrapper.clients = this.notificationRuleToAdd?.clients ?? formValue.clients;
    wrapper.processNodes =
      this.notificationRuleToAdd?.processNodes ?? formValue.processNodes;
    wrapper.statuses = formValue.statuses;
  }

  private createFormGroup(
    ruleWrapper: NotificationRuleWorkItemWrapper
  ): UntypedFormGroup {
    return this.formBuilder.group({
      id: [ruleWrapper.id],
      name: [ruleWrapper.name, Validators.required],
      clients: [ruleWrapper.clients],
      processNodes: [ruleWrapper.processNodes],
      statuses: [ruleWrapper.statuses]
    });
  }
}

interface RuleFormValue {
  id: number;
  name: string;
  clients: ClientDto[];
  processNodes: ProcessElementDto[];
  statuses: number[];
}

interface FormStatus {
  name: string;
  value: number;
}
